import { useEffect, useState } from "react";
import { ASSETS, CONTACT } from "@/lib/constants";
import { Menu, X, Phone } from "lucide-react";
import { useApplyIntent } from "@/components/ApplyIntent";
import { useRateCheckRouter } from "@/components/RateCheckRouter";
import { useMLO } from "@/lib/mloContext";
import { resolveApplyUrl, applyPagePath } from "@/lib/applyTarget";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openIntent } = useApplyIntent();
  const { openRouter } = useRateCheckRouter();
  const mlo = useMLO();
  const applyUrl = resolveApplyUrl(mlo);
  const intentOpts = { applyUrl, mloSlug: mlo?.slug, mloName: mlo?.name, mloPhone: mlo?.phone };

  // Phone: MLO record if we're on a branded page, otherwise fall back to Jason's number.
  const phoneDisplay = mlo?.phone || CONTACT.phoneDisplay;
  const phoneHref = mlo?.phone
    ? `tel:${mlo.phone.replace(/[^+0-9]/g, "")}`
    : CONTACT.phoneHref;
  const firstName = (mlo?.name || CONTACT.loanOfficer).split(" ")[0];

  const navLinks = [
    { label: "Why", href: "#why" },
    { label: "How it works", href: "#how" },
    { label: `Meet ${firstName}`, href: "#jason" },
    { label: "FAQ", href: "#faq" },
    applyUrl
      ? { label: "Apply", href: applyPagePath(mlo) }
      : { label: `Call ${firstName}`, href: phoneHref },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-linen/85 border-b border-linen-border/70 shadow-[0_1px_0_rgba(15,42,71,0.03)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-grc flex items-center justify-between h-16 sm:h-20">
        <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-2xl transition-all duration-300 ${
              scrolled ? "bg-transparent px-0 py-0" : "bg-white/95 backdrop-blur px-3 py-1.5 shadow-soft"
            }`}
          >
            <img
              src={ASSETS.logoHorizontal}
              alt="Great Rate Check"
              className="h-9 sm:h-11 w-auto"
              loading="eager"
            />
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-navy/80 hover:text-teal transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-teal after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Phone link — only rendered when we're on a personalized MLO page.
              The homepage has no specific MLO to call, so we skip it there
              to avoid showing a misleading generic number. Text color adapts
              to the scroll state so it's readable on both the transparent
              hero background and the scrolled cream background. */}
          {mlo?.phone && (
            <>
              <a
                href={phoneHref}
                data-testid="nav-phone-link"
                className={`hidden md:inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  scrolled ? "text-navy/80 hover:text-teal" : "text-white/90 hover:text-lime"
                }`}
                aria-label={`Call ${firstName}`}
              >
                <Phone size={15} strokeWidth={2} />
                <span className="tabular-nums">{phoneDisplay}</span>
              </a>
              <a
                href={phoneHref}
                data-testid="nav-phone-mobile"
                className={`md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${
                  scrolled ? "bg-teal/10 text-teal hover:bg-teal/20" : "bg-white/15 text-white hover:bg-white/25"
                }`}
                aria-label={`Call ${firstName}`}
              >
                <Phone size={17} strokeWidth={2} />
              </a>
            </>
          )}
          <button
            type="button"
            data-testid="nav-cta-button"
            onClick={() => mlo ? openIntent("nav-cta-button", intentOpts) : openRouter("nav-cta-button")}
            className="hidden sm:inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-sm px-5 py-2.5 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
          >
            Check My Rate
            <span aria-hidden>→</span>
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 -mr-2 text-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-linen-border/70 bg-linen/95 backdrop-blur-xl">
          <div className="container-grc py-4 flex flex-col gap-1">
            {mlo?.phone && (
              <a
                href={phoneHref}
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-phone"
                className="mb-1 flex items-center justify-between py-3 px-3 rounded-xl bg-teal/10 text-teal font-semibold border border-teal/20"
              >
                <span className="inline-flex items-center gap-2">
                  <Phone size={16} strokeWidth={2.25} />
                  Call {firstName}
                </span>
                <span className="tabular-nums text-sm">{phoneDisplay}</span>
              </a>
            )}
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="py-3 text-navy font-medium border-b border-linen-border/60"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => { setOpen(false); mlo ? openIntent("nav-mobile-cta", intentOpts) : openRouter("nav-mobile-cta"); }}
              data-testid="nav-mobile-cta"
              className="mt-3 inline-flex justify-center items-center gap-2 bg-lime text-navy-deep font-semibold px-5 py-3 rounded-full shadow-cta"
            >
              Check My Rate →
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
